import { ComponentProps, forwardRef } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { Fade, styled, TextField as MuiTextField } from '@mui/material'

const sizeMap = {
  small: {
    height: 32,
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    borderRadius: 4,
    fontSize: 12,
    labelSize: 12,
  },
  medium: {
    height: 36,
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    borderRadius: 8,
    fontSize: 14,
    labelSize: 14,
  },
  large: {
    height: 48,
    padding: `${pxToRem(12)} ${pxToRem(16)}`,
    borderRadius: 12,
    fontSize: 16,
    labelSize: 14,
  },
} as const

export type TextFieldProps = Omit<ComponentProps<typeof MuiTextField>, 'variant' | 'round' | 'color' | 'size'> & {
  round?: boolean
  size?: keyof typeof sizeMap
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({ round, size = 'medium', ...props }, ref) => (
  <StyledOutlineTextField
    variant="outlined"
    inputRef={ref}
    $round={round}
    $size={size}
    fullWidth
    margin="normal"
    InputLabelProps={{
      shrink: true,
      sx: {
        display: 'flex',
        alignItems: 'center',
      },
    }}
    SelectProps={{
      MenuProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        TransitionComponent: Fade,
      },
    }}
    {...props}
  />
))

const StyledOutlineTextField = styled(MuiTextField, {
  shouldForwardProp: (prop) => !['$round', '$size'].includes(prop as string),
}) <{ $round?: boolean; $size: keyof typeof sizeMap }>`
  width: 100%;
  border-radius: ${(props) => props.$round
    ? props.theme.typography.pxToRem(1000)
    : props.theme.typography.pxToRem(sizeMap[props.$size].borderRadius)};
  margin-top: ${(props) => props.theme.typography.pxToRem(8)};


  &.MuiTextField-root {
    margin: 0 !important;
  }

  & .MuiInputLabel-root {
    font-size: ${(props) => props.theme.typography.pxToRem(sizeMap[props.$size].labelSize)};
    font-weight: 500;
    position: relative;
    transform: none;
    margin-bottom: ${pxToRem(3)};
  }

  .MuiAutocomplete-input {
    width: 100% !important;
  }

  .MuiOutlinedInput-root {
    font-size: ${(props) => props.theme.typography.pxToRem(sizeMap[props.$size].fontSize)};
    border-radius: ${(props) => props.$round ? '1000px' : props.theme.typography.pxToRem(sizeMap[props.$size].borderRadius)} !important;
    background-color: ${(props) => props.theme.palette.custom.input.backgroundColor};
    padding: 0;

    & .MuiAutocomplete-input {
      padding: 0 0 0 ${pxToRem(12)} !important;
    }

    input {
      height: ${props => pxToRem(sizeMap[props.$size].height)};
      line-height: normal;
      padding: 0 0 0 ${pxToRem(12)};
    }

    & .MuiSelect-outlined {
      display: flex;
      align-items: center;
      height: ${props => pxToRem(sizeMap[props.$size].height)};
      line-height: normal;
      padding: 0 0 0 ${pxToRem(12)};
    }
  }
  
  .MuiInputAdornment-root.MuiInputAdornment-positionStart {
    margin-left: ${pxToRem(12)};
    margin-right: 0;
  }

  .MuiInputAdornment-root.MuiInputAdornment-positionEnd {
    margin-left: 0;
    margin-right: ${pxToRem(12)};
  }

  .MuiOutlinedInput-notchedOutline {
    top: 0;
    left: 0;
    > legend {
      display: none; 
    }
  }

  .MuiSvgIcon-root {
    color: ${(props) => props.theme.palette.text.primary};
  }

  .Mui-error.Mui-focused fieldset {
    box-shadow: 0 0 0 2px ${(props) => props.theme.palette.background.paper}, 0 0 0 3px rgba(211, 47, 47, 0.4);    
  }

  .Mui-disabled {
    .MuiSvgIcon-root {
      color: ${(props) => props.theme.palette.text.disabled};
    }
  }

  .MuiInputBase-multiline {
    height: unset;

    textarea {
      padding: ${pxToRem(11.5)} 0 ${pxToRem(11.5)} ${pxToRem(12)};
    }
  }

  .MuiFormHelperText-root {
    margin-left: 0;
  }
`
